import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../store';
import useTranslations from '../../../hooks/useTranslations';
import usePermissionRedirect from '../../../hooks/usePermissionRedirect';
import { FieldTypes, IFormFields } from '../../../models/form';
import Form from '../../../components/Container/Form/Form';
import Alert from '../../../components/UI/Alert/Alert';
import BackButton from '../../../components/UI/BackButton/BackButton';
import { IPage } from '../../../models/page';
import { ActionPermissionsActions } from '../../../store/entities/actionPermissions/actionPermissions.actions';
import { IActionPermission } from '../../../models/actionPermission';
import { CONSTANTS } from '../../../utils/constants';
import { ACTION_PERMISSION_STATUS_OPTIONS, EDITABLE_ACTION_PERMISSION_STATUS_OPTIONS } from '../actionPermissionsConstants';
import * as Yup from 'yup';

const updateActionPermissionValidation = Yup.object().shape({
  status: Yup.string().optional(),
  usesLimit: Yup.number()
    .nullable()
    .optional()
    .min(1, 'validation.usesLimitMin'),
  expiryDate: Yup.string()
    .nullable()
    .optional()
});

const UpdateActionPermission = ({ permission, nextRoute }: IPage) => {
  const [show, setShow] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [existing, setExisting] = useState<IActionPermission | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rcTranslate } = useTranslations();
  const { id } = useParams<{ id: string }>();
  usePermissionRedirect(permission, nextRoute);

  const { actionPermissions } = useAppSelector((state) => state.actionPermissions);

  useEffect(() => {
    const numericId = Number(id);
    // Use cached data if already in store, otherwise fetch from BE
    const cached = actionPermissions.find((ap) => ap.id === numericId);
    if (cached) {
      setExisting(cached);
    } else {
      (dispatch(ActionPermissionsActions.getActionPermissionById(numericId)) as unknown as Promise<IActionPermission | null>).then(
        (data) => {
          if (data) setExisting(data);
        }
      );
    }
  }, [id]);

  const currentStatusKey = ACTION_PERMISSION_STATUS_OPTIONS.find((o) => o.key === existing?.status)?.key ?? '';

  const fields: IFormFields = useMemo(
    () => ({
      initialValues: {
        status: currentStatusKey,
        usesLimit: existing?.usesLimit ?? '',
        expiryDate: existing?.expiryDate ?? '',
      },
      formData: [
        {
          type: FieldTypes.SELECT,
          id: 'status',
          label: 'actionPermissions.form.status',
          items: EDITABLE_ACTION_PERMISSION_STATUS_OPTIONS,
        },
        {
          type: FieldTypes.NUMBER,
          id: 'usesLimit',
          label: 'actionPermissions.form.usesLimit',
        },
        {
          type: FieldTypes.DATE,
          id: 'expiryDate',
          label: 'actionPermissions.form.expiryDate',
        },
      ],
    }),
    [existing]
  );

  const onSubmit = async (values: any, setSubmitting: (v: boolean) => void) => {
    const result = await dispatch(
      ActionPermissionsActions.updateActionPermission(Number(id), {
        status: values.status || null,
        usesLimit: values.usesLimit || null,
        expiryDate: values.expiryDate || null,
      })
    );

    setSubmitting(false);

    if (result !== undefined) {
      setAlertMessage(rcTranslate('actionPermissions.form.updateSuccess'));
      setShow(true);
      setTimeout(() => {
        setShow(false);
        navigate(
          '/' +
            CONSTANTS.ROUTES.ACTION_PERMISSIONS_MANAGEMENT.BASE +
            '/' +
            CONSTANTS.ROUTES.ACTION_PERMISSIONS_MANAGEMENT.CHILDREN.ACTION_PERMISSIONS_LIST
        );
      }, 2500);
    }
  };

  return (
    <>
      <BackButton />
      <Form
        fields={fields}
        formValidation={updateActionPermissionValidation}
        onSubmit={onSubmit}
        submitButtonTitle={rcTranslate('actionPermissions.form.updateButton')}
        entityIdentifier={{
          label: 'actionPermissions.form.entityIdentifier',
          id: id ?? '',
        }}
      />
      {show && <Alert message={alertMessage} color="success" />}
    </>
  );
};

export default UpdateActionPermission;
